import { Dropdown } from "@src/components/dropdown/Dropdown";
import { DateInput } from "@src/components/input/DateInput";
import { Input } from "@src/components/input/Input";
import { SelectYup } from "@src/components/select/SelectYup";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { OptionType } from "@src/components/utils/reactSelectUtils";
import { filtersEmptyValues } from "@src/components/utils/hookFormUtils";
import { systemActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { HeaderFiltersProps } from "@src/validation/HeaderFiltersValidation";
import React, { useEffect, useState } from "react";
import { Control, UseFormGetValues, UseFormReset, UseFormSetValue } from "react-hook-form";
import { FilterField } from "@src/components/filtersPanel/FilterField";
import { FilterSection } from "@src/components/filtersPanel/FilterSection";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { decodeSmartSearchValue, SMART_SEARCH_SUGGESTION_LIMIT } from "@src/components/utils/smartSearchUtils";

const formatSmartSearchValue = (value: string) => decodeSmartSearchValue(value).term;

export interface IAllFilters {
	control: Control<HeaderFiltersProps>;
	setValue: UseFormSetValue<HeaderFiltersProps>;
	getValues: UseFormGetValues<HeaderFiltersProps>;
	reset: UseFormReset<HeaderFiltersProps>;
	savedFilters?: Array<HeaderFiltersProps>;
	categoryOptions: Array<OptionType>;
	headlinerOptions: Array<OptionType>;
	venueOptions: Array<OptionType>;
	smartSearchOptions: Array<OptionType>;
	companyOptions: Array<OptionType>;
	companiesLoading: boolean;
	isAdmin: boolean;
	onSaveFilters: () => void;
}

export const PricingFilters: React.FC<IAllFilters> = ({
	control,
	setValue,
	getValues,
	reset,
	savedFilters,
	categoryOptions,
	headlinerOptions,
	venueOptions,
	smartSearchOptions,
	companyOptions,
	companiesLoading,
	isAdmin,
	onSaveFilters,
}) => {
	const [isAdvancedOpen, setIsAdvancedOpen] = useState(true);

	const { data: allTags, loading: tagsLoading } = useAppSelector((state) => state.system.allTags);
	const dispatch = useAppDispatch();

	const tagOptions = allTags.map((i) => Object.values(i)).map(([x]) => ({ label: x, value: x }));

	useEffect(() => {
		if (allTags.length) return;
		dispatch(systemActions.listAllTags({}));
	}, [dispatch, allTags.length]);

	const savedSearchItems: Array<{ id: number; label: string; onClick: () => void }> = [
		{ id: 0, label: "Select", onClick: () => reset({ ...filtersEmptyValues, companyName: getValues("companyName") }) },
		{ id: 1, label: "Save current filters", onClick: onSaveFilters },
		...(savedFilters ?? []).map((item, index) => ({
			id: index + 2,
			label: `Saved Filters ${index + 1}`,
			onClick: () => {
				reset(filtersEmptyValues);
				(Object.entries(item) as Array<[keyof HeaderFiltersProps, string]>).forEach(
					([key, value]) => value && setValue(key, value, { shouldDirty: true }),
				);
			},
		})),
	];

	return (
		<>
			<FilterSection title="Search">
				{isAdmin ? (
					<FilterField label="Company name">
						<SelectYup
							onFocus={() => mixpanelTrack(MixpanelCategory.COMPANY_NAME_FILTER)}
							isLoading={companiesLoading}
							name="companyName"
							placeholder="Search by company name"
							control={control}
							options={companyOptions}
						/>
					</FilterField>
				) : null}
				<FilterField label="Smart search">
					<SelectYup
						name="smartSearch"
						placeholder="Search by any parameter"
						control={control}
						options={smartSearchOptions}
						allowCustomInput={true}
						hideDropdownIndicator={true}
						maxVisibleOptions={SMART_SEARCH_SUGGESTION_LIMIT}
						menuMaxHeight={300}
						onFocus={() => mixpanelTrack(MixpanelCategory.SMART_SEARCH_FILTER)}
						formatValueLabel={formatSmartSearchValue}
					/>
				</FilterField>

				<FilterField label="Tags">
					<SelectYup
						onFocus={() => mixpanelTrack(MixpanelCategory.TAGS_FILTER)}
						name="tags"
						placeholder="Search by tags"
						isLoading={tagsLoading}
						control={control}
						options={tagOptions}
					/>
				</FilterField>

				<FilterField label="Inventory tags">
					<SelectYup
						onFocus={() => mixpanelTrack(MixpanelCategory.TAGS_FILTER)}
						name="inventoryTags"
						placeholder="Search by inventory tags"
						isLoading={tagsLoading}
						control={control}
						options={tagOptions}
					/>
				</FilterField>
			</FilterSection>

			<FilterSection title="Event">
				<div className={styles.row}>
					<FilterField label="Start date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="startDate"
							placeholder="Start date"
							popperPlacement="bottom-start"
							onFocus={() => mixpanelTrack(MixpanelCategory.START_DATE_FILTER)}
							onChange={(value: string) => {
								if (value && !getValues("endDate"))
									setValue("endDate", value, { shouldDirty: true, shouldValidate: true });
							}}
						/>
					</FilterField>
					<FilterField label="End date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="endDate"
							placeholder="End date"
							popperPlacement="bottom-end"
							onFocus={() => mixpanelTrack(MixpanelCategory.END_DATE_FILTER)}
							onChange={(value: string) => {
								if (value && !getValues("startDate"))
									setValue("startDate", value, { shouldDirty: true, shouldValidate: true });
							}}
						/>
					</FilterField>
				</div>

				<FilterField label="Event category">
					<SelectYup name="category" placeholder="Select event category" control={control} options={categoryOptions} />
				</FilterField>

				<FilterField label="Event headliner">
					<SelectYup
						name="eventHeadliner"
						placeholder="Select event headliner"
						control={control}
						options={headlinerOptions}
						onFocus={() => mixpanelTrack(MixpanelCategory.HEADLINER_FILTER)}
					/>
				</FilterField>

				<FilterField label="Event venue">
					<SelectYup
						name="venue"
						placeholder="Select event venue"
						control={control}
						options={venueOptions}
						onFocus={() => mixpanelTrack(MixpanelCategory.VENUE_FILTER)}
					/>
				</FilterField>
			</FilterSection>

			<FilterSection title="Ticket">
				<div className={styles.row}>
					<FilterField label="Section">
						<Input
							onFocus={() => mixpanelTrack(MixpanelCategory.TICKET_SECTION)}
							control={control}
							name="section"
							placeholder="e.g. 133-144, 108-118"
						/>
					</FilterField>
					<FilterField label="Row">
						<Input
							onFocus={() => mixpanelTrack(MixpanelCategory.TICKET_ROW)}
							control={control}
							name="row"
							placeholder="Enter ticket row"
						/>
					</FilterField>
				</div>

				<FilterField label="Minimum quantity">
					<Input
						onFocus={() => mixpanelTrack(MixpanelCategory.TICKET_MIN_QUANTITY)}
						control={control}
						type="number"
						name="minQuantity"
						placeholder="Enter minimum ticket quantity"
					/>
				</FilterField>

				<FilterField label="Last update">
					<Input
						onFocus={() => mixpanelTrack(MixpanelCategory.TICKET_DAYS_SINCE_LAST_UPDATE)}
						control={control}
						type="number"
						name="daysSinceLastUpdate"
						placeholder="Number of days since last update"
					/>
				</FilterField>
			</FilterSection>

			<FilterSection
				title="Advanced"
				isCollapsible
				isOpen={isAdvancedOpen}
				onToggle={() => setIsAdvancedOpen((prev) => !prev)}>
				<div className={styles.row}>
					<FilterField label="Listing ID">
						<Input
							onFocus={() => mixpanelTrack(MixpanelCategory.ADVANCED_LISTING_ID)}
							name="listingId"
							placeholder="Enter listing ID"
							control={control}
						/>
					</FilterField>
					<FilterField label="Event ID">
						<Input
							onFocus={() => mixpanelTrack(MixpanelCategory.ADVANCED_EVENT_ID)}
							name="eventId"
							placeholder="Enter event ID"
							control={control}
						/>
					</FilterField>
				</div>

				<FilterField label="PO ID">
					<Input
						onFocus={() => mixpanelTrack(MixpanelCategory.ADVANCED_PO_ID)}
						name="poId"
						placeholder="Enter PO ID"
						control={control}
					/>
				</FilterField>

				<div className={styles.row}>
					<FilterField label="PO start date">
						<DateInput
							popperStrategy="fixed"
							onFocus={() => mixpanelTrack(MixpanelCategory.ADVANCED_PO_START_DATE)}
							control={control}
							name="poStartDate"
							placeholder="Start date"
							popperPlacement="bottom-start"
						/>
					</FilterField>
					<FilterField label="PO end date">
						<DateInput
							popperStrategy="fixed"
							onFocus={() => mixpanelTrack(MixpanelCategory.ADVANCED_PO_END_DATE)}
							control={control}
							name="poEndDate"
							placeholder="End date"
							popperPlacement="bottom-end"
						/>
					</FilterField>
				</div>
			</FilterSection>

			<div className={styles.savedSearch}>
				<div className={styles.fieldLabel}>Saved search</div>
				<Dropdown
					dynamicText
					isDisabled={false}
					buttonText="Select saved search"
					containerWidth="100%"
					items={savedSearchItems}
				/>
			</div>
		</>
	);
};
